import { waterEventStateInfo } from "utils/waterEvents";
import { duration, fieldTime } from "utils/fieldFormat";
import { formatMm } from "utils/radarDiagnostics";
import {
  ESTIMATOR_NAMES,
  doubtText,
  finiteNumber,
  formatCm,
  minutesText,
  stepText,
} from "utils/radarLab";
import { More } from "components/field/radarUi";
import { EmergencyPumpStop } from "./LabActions";

const ago = (seconds) => {
  const s = finiteNumber(seconds);
  if (s === null) return "never";
  if (s < 90) return `${Math.round(s)} s ago`;
  if (s < 5400) return `${Math.round(s / 60)} min ago`;
  return `${Math.round(s / 3600)} h ago`;
};
const when = (value) => (value ? `${fieldTime(value)} SGT` : "—");

function Facts({ rows }) {
  return (
    <dl className="field-lab-facts">
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

// The tank side: which greenhouse controller, its pump, its telemetry, its
// camera and any pump operation its own engine has in flight (the Experiment
// page's operations included).
export function TankPanel({ status, controllers, controllerMac, onController, locked, onPumpStopped }) {
  const pump = status?.pump || null;
  const telemetry = status?.controller_telemetry || null;
  const camera = status?.camera || null;
  const inFlight = status?.engine_in_flight || [];
  const controller = status?.controller || null;
  return (
    <section className="field-panel" aria-labelledby="lab-tank-title">
      <div className="field-panel-head">
        <div>
          <h2 id="lab-tank-title">Tank and pump</h2>
          <p>The greenhouse controller whose pump moves the water, and its camera.</p>
        </div>
      </div>
      <div className="field-panel-body">
        <div className="field-lab-select">
          <label htmlFor="lab-controller">Greenhouse controller</label>
          <select
            id="lab-controller"
            value={controllerMac || ""}
            disabled={locked || !controllers.length}
            onChange={(e) => onController(e.target.value)}
          >
            {!controllerMac && <option value="">Select a controller</option>}
            {controllers.map((c) => (
              <option key={c.mac} value={c.mac}>
                {c.name || c.mac}
              </option>
            ))}
          </select>
        </div>
        <Facts
          rows={[
            [
              "Pump",
              pump?.status
                ? `${pump.status === "running" ? "Running" : "Stopped"}${pump.direction ? ` (${pump.direction})` : ""}${pump.reason ? `, ${pump.reason}` : ""}${pump.stale ? " — this state looks stale" : ""}`
                : "No pump report yet",
            ],
            ["Pump report", when(pump?.updated_at)],
            [
              "Controller telemetry",
              telemetry?.received_at
                ? `${ago(telemetry.age_s)}${telemetry.pump_state ? ` · pump ${telemetry.pump_state.toLowerCase()}` : ""}`
                : "None received",
            ],
            [
              "Camera",
              camera
                ? `${camera.name || camera.mac}${camera.active_runs?.length ? ` · active camera runs: ${camera.active_runs.join(", ")}` : " · idle"}`
                : "No camera linked to this controller",
            ],
          ]}
        />
        {inFlight.length > 0 && (
          <div className="field-lab-hold" style={{ marginTop: 14 }} role="status">
            <b>Pump operations in progress:</b>{" "}
            {inFlight
              .map(
                (op) =>
                  `${stepText(op)} · ${waterEventStateInfo(op.state).label}${op.run_id ? ` (${op.run_id})` : op.source === "manual" ? " (by hand)" : ""}`,
              )
              .join("; ")}
            . No lab step starts while any is in flight.
          </div>
        )}
        {pump?.status === "running" && controller && (
          <div style={{ marginTop: 14 }}>
            <EmergencyPumpStop controller={controller} onDone={onPumpStopped} />
          </div>
        )}
      </div>
    </section>
  );
}

// Everything that must hold before a lab run can start, as the server checks it.
export function ReadinessPanel({ status }) {
  const checks = status?.checks || [];
  const node = status?.node_state || null;
  const stream = node?.stream || {};
  const failing = checks.filter((c) => !c.ok).length;
  return (
    <section className="field-panel" aria-labelledby="lab-ready-title">
      <div className="field-panel-head">
        <div>
          <h2 id="lab-ready-title">Ready to start?</h2>
          <p>Checked by the server again before every pump step.</p>
        </div>
        <span className={`field-state ${status?.ready ? "is-running" : "is-waiting"}`}>
          {status?.ready ? "Ready" : `${failing || "Some"} check${failing === 1 ? "" : "s"} not met`}
        </span>
      </div>
      <div className="field-panel-body">
        <ul className="field-lab-checks" aria-label="Readiness checks">
          {checks.map((check) => (
            <li
              key={check.key}
              className={`field-lab-check ${check.ok ? "is-ok" : "is-fail"}`}
            >
              <span
                className="field-lab-mark"
                role="img"
                aria-label={check.ok ? "met" : "not met"}
              >
                {check.ok ? "✓" : "✗"}
              </span>
              <span>
                {check.label || check.key}
                {check.detail && <small>{check.detail}</small>}
              </span>
            </li>
          ))}
        </ul>
        {node && (
          <div style={{ marginTop: 18 }}>
            <Facts
              rows={[
                ["Node's last reading", `${ago(node.age_s)}${node.radar_valid === false ? " · radar not valid" : ""}`],
                [
                  "Live mode",
                  node.live_phase
                    ? `${node.live_phase}${node.interval_s ? ` · every ${node.interval_s} s` : ""}${node.external_power === false ? " · on battery" : ""}`
                    : "—",
                ],
                [
                  "Radar stream",
                  stream.radar_stream !== undefined
                    ? `mask ${stream.radar_stream}${stream.radar_raw_alt ? ", alternating" : ""}${stream.radar_stream_s ? `, every ${stream.radar_stream_s} s` : ""}`
                    : "Not reported",
                ],
                ["Newest capture", when(node.last_capture_at)],
              ]}
            />
          </div>
        )}
      </div>
    </section>
  );
}

const SOURCES = {
  greenhouse_run: (m) => `the Experiment run ${m.run_id || ""} (its logged levels)`,
  lab_run: (m) => `lab run #${m.lab_run_id ?? "?"}`,
  ruler: (m) =>
    `a ruler reading${finiteNumber(m.ruler_level_cm) !== null ? ` of ${formatCm(m.ruler_level_cm)}` : ""}`,
};

// The node's three estimators of its air gap over the last minute and their
// vote: two or three that agree decide; one that agrees with neither is left out.
function EstimatorsLine({ estimators }) {
  const e = estimators || {};
  const parts = [
    ["beat_signal", e.beat_air_mm],
    ["spectrum", e.spectrum_air_mm],
    ["radar", e.radar_air_mm],
  ].filter(([, value]) => finiteNumber(value) !== null);
  if (!parts.length) return null;
  const agreed = e.agreed || [];
  return (
    <p className="field-caption" style={{ marginTop: 4 }} aria-label="Estimators">
      {parts
        .map(([key, value]) => {
          const mark = e.dropped === key ? " (left out)" : agreed.length > 1 && agreed.includes(key) ? " ✓" : "";
          return `${ESTIMATOR_NAMES[key]} ${formatMm(value, 1)}${mark}`;
        })
        .join(" · ")}
      {e.undecided ? " · they disagree" : ""}
      {(e.low_confidence || []).length ? ` · doubtful: ${doubtText(e.low_confidence)}` : ""}
    </p>
  );
}

// The server's limits on the water and on a lab run (its overflow guards
// among them), as far as it sends them.
function limitRows(limits) {
  const l = limits || {};
  const n = (key) => finiteNumber(l[key]);
  const rows = [];
  if (n("tank_min_cm") !== null && n("tank_max_cm") !== null)
    rows.push(["Tank bounds", `${formatCm(l.tank_min_cm, 2)} to ${formatCm(l.tank_max_cm, 2)}`]);
  if (n("tank_ceiling_cm") !== null)
    rows.push([
      "Ceiling",
      `${formatCm(l.tank_ceiling_cm, 2)} on the ruler: no fill that could pass it is planned or pumped${n("ceiling_factor_spread") !== null ? ` (at the pump's factor plus ${l.ceiling_factor_spread})` : ""}; a ruler reading above it${n("ceiling_confidence_min") !== null ? `, ${l.ceiling_confidence_min} confident or more,` : ""} stops a lab run at once`,
    ]);
  if (n("max_drain_cm") !== null) rows.push(["Deepest drain", `${formatCm(l.max_drain_cm)} below a lab run's start`]);
  if (n("target_tolerance_cm") !== null)
    rows.push(["Target steps", `A hit within ±${l.target_tolerance_cm} cm of the target`]);
  if (n("step_cm_min") !== null && n("step_cm_max") !== null)
    rows.push([
      "Steps",
      `${formatCm(l.step_cm_min)} to ${formatCm(l.step_cm_max)} each${n("steps_max") !== null ? `, at most ${l.steps_max} in a lab run` : ""}`,
    ]);
  if (n("run_max_s") !== null) rows.push(["A lab run", `At most ${duration(l.run_max_s)}, back at its start level`]);
  return rows;
}

// The tank level now, from node 1's air gap through the reference mapping,
// the ruler reading that can replace it for the start, and the limits.
export function LevelPanel({ level, limits, ruler, onRuler, rulerError, disabled }) {
  const mapping = level?.mapping || null;
  const source = mapping ? (SOURCES[mapping.source] || (() => mapping.source))(mapping) : null;
  const limitsShown = limitRows(limits);
  return (
    <section className="field-panel" aria-labelledby="lab-level-title">
      <div className="field-panel-head">
        <div>
          <h2 id="lab-level-title">Tank level</h2>
          <p>
            Node 1's air gap, mapped through a known level · bounds {formatCm(limits?.tank_min_cm, 0)} to{" "}
            {formatCm(limits?.tank_max_cm, 0)}
            {finiteNumber(limits?.tank_ceiling_cm) !== null ? `, ceiling ${formatCm(limits.tank_ceiling_cm, 2)}` : ""}
          </p>
        </div>
      </div>
      <div className="field-panel-body field-lab-level">
        <strong>{level?.ok && finiteNumber(level?.level_cm) !== null ? formatCm(level.level_cm) : "Unknown"}</strong>
        <p className="field-caption" style={{ marginTop: 6 }}>
          {finiteNumber(level?.air_mm) !== null
            ? `Air gap ${formatMm(level.air_mm, 1)} (median of ${level.readings ?? "?"} readings).`
            : "No recent valid reading from the node."}
          {source
            ? ` Reference: ${source}, ${mapping.points ?? "?"} point${mapping.points === 1 ? "" : "s"}${finiteNumber(mapping.spread_cm) !== null ? `, spread ${formatCm(mapping.spread_cm, 2)}` : ""}${mapping.at ? `, ${fieldTime(mapping.at)} SGT` : ""}.`
            : ""}
        </p>
        <EstimatorsLine estimators={level?.estimators} />
        {level?.detail && (
          <div className="field-notice" style={{ marginTop: 10 }}>
            {level.detail}
          </div>
        )}
        <div className="field-lab-input">
          <label htmlFor="lab-ruler">Ruler reading for the start (cm, optional)</label>
          <input
            id="lab-ruler"
            inputMode="decimal"
            value={ruler}
            disabled={disabled}
            aria-invalid={rulerError ? "true" : "false"}
            onChange={(e) => onRuler(e.target.value)}
            placeholder="e.g. 27.5"
          />
          <small className="field-caption" style={{ marginTop: 0 }}>
            {rulerError ||
              "Read on the tank's ruler: it replaces the derived level as the reference for the next lab run."}
          </small>
        </div>
        {limitsShown.length > 0 && (
          <div style={{ marginTop: 16 }} role="group" aria-label="Limits">
            <More label="Limits: what the server never lets a lab run do">
              <Facts rows={limitsShown} />
            </More>
          </div>
        )}
      </div>
    </section>
  );
}

function Meter({ value, max }) {
  const v = finiteNumber(value) ?? 0;
  const m = finiteNumber(max) ?? 0;
  const pct = m > 0 ? Math.min(100, (v / m) * 100) : 0;
  return (
    <div className={`field-lab-meter${pct >= 100 ? " is-full" : ""}`} aria-hidden="true">
      <span style={{ width: `${pct}%` }} />
    </div>
  );
}

// Work up in steps: each tier opens with a clean lab run of the tier below,
// and the day's budgets keep the tank and the pump from being overworked.
export function TiersBudgetsPanel({ tiers, budgets }) {
  const b = budgets || {};
  return (
    <section className="field-panel" aria-labelledby="lab-tiers-title">
      <div className="field-panel-head">
        <div>
          <h2 id="lab-tiers-title">Tiers and today's budget</h2>
          <p>Each tier opens after a clean lab run of the tier below.</p>
        </div>
      </div>
      <div className="field-panel-body">
        <ul className="field-lab-tiers" aria-label="Tiers">
          {(tiers || []).map((tier) => (
            <li key={tier.tier} className="field-lab-tier">
              <span>
                <b>Tier {tier.tier}</b>
                <small>
                  {tier.unlocked ? "Open" : tier.detail || "Locked"}
                  {tier.clean_runs ? ` · ${tier.clean_runs} clean lab run${tier.clean_runs === 1 ? "" : "s"}` : ""}
                </small>
              </span>
              <span className={`field-state ${tier.unlocked ? "is-running" : "is-off"}`}>
                {tier.unlocked ? "Open" : "Locked"}
              </span>
            </li>
          ))}
        </ul>
        <div className="field-lab-budget">
          <span>
            Lab runs today: <b>{b.runs_today ?? 0}</b> of {b.runs_per_day_max ?? "—"}
            {b.day ? ` (${b.day}, SGT)` : ""}
          </span>
          <Meter value={b.runs_today} max={b.runs_per_day_max} />
        </div>
        <div className="field-lab-budget">
          <span>
            Lab pumping today: <b>{minutesText(b.lab_pump_s_today ?? 0)}</b> of{" "}
            {minutesText(b.lab_pump_s_max)}
          </span>
          <Meter value={b.lab_pump_s_today} max={b.lab_pump_s_max} />
        </div>
        <div className="field-lab-budget">
          <span>
            All pumping on the controller today (Experiment runs included):{" "}
            <b>{minutesText(b.controller_pump_s_today ?? 0)}</b> of{" "}
            {minutesText(b.controller_pump_s_max)}
          </span>
          <Meter value={b.controller_pump_s_today} max={b.controller_pump_s_max} />
        </div>
        <p className="field-caption">
          {b.next_start_at
            ? `Next lab run from ${fieldTime(b.next_start_at)} SGT (a gap after the last one).`
            : "No gap to wait for."}{" "}
          {b.start_window
            ? `Lab runs start between ${b.start_window} SGT${b.in_window === false ? " — outside that window now" : ""}.`
            : "No start window is set."}
        </p>
      </div>
    </section>
  );
}
